<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'
import { useCurrencyColumnFormatter } from '@/composables/data-grid'
import { useChips } from '@/composables/chips'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { useNavigator } from '@/composables/navigator'
import { usePdfPreview } from '@/composables/pdf-preview'
import { useStepper } from '@/composables/stepper'
import { usePrintSettings, useReportColumnTemplates } from '@/composables/report-column-template'
import { useWebSiteStore } from '@/stores/web-site.store'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import GridDetailInfo from '@/components/shared/GridDetailInfo.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import PdfPreviewPanel from '@/components/shared/PdfPreviewPanel.vue'
import PrintDialogLayout from '@/components/shared/PrintDialogLayout.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import PrintConfigurator from '@/components/accounting/PrintConfigurator.vue'
import PrintContentEditor from '@/components/accounting/PrintContentEditor.vue'

const props = defineProps({
  invoiceId: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['cancel'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { step, nextStep, prevStep } = useStepper()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const { getInvoiceStatusChips, getProjectChips } = useChips()

useNavigator(() => emit('cancel'))

const invoice = ref(null)
const config = ref(null)
const templateId = ref(null)
const printTemplateId = ref(null)
const language = ref(null)
const { documentOptions } = useReportColumnTemplates()
const settings = usePrintSettings({ config, templateId, printTemplateId, defaultPrintTemplateId: webSiteStore.getDefaultInvoicePrintTemplate() })

const summary = computed(() => [
  { label: i18n.t('common.invoiceNumber'), value: invoice.value.invoice_number },
  { label: i18n.t('common.date'), value: formatShortDate(invoice.value.date) },
  { label: i18n.t('common.company'), value: invoice.value.company_name },
  { label: i18n.t('common.totalNoVat'), value: useCurrencyColumnFormatter(invoice.value, 'total_no_vat', invoice.value.currency_ref) },
  { label: i18n.t('common.totalVat'), value: useCurrencyColumnFormatter(invoice.value, 'total_vat', invoice.value.currency_ref) },
].filter(item => item.value))

const isDraft = computed(() => invoice.value?.status === accountingStore.invoiceStatusEnum.DRAFT)
const lockedMessage = computed(() => (isDraft.value ? null : i18n.t('info.printTemplateInvoiceLocked')))
const { blobUrl, source: pdfSource, isLoading, isPrinting, refreshAsync: refreshPreview, printAsync } = usePdfPreview(
  () => accountingStore.previewInvoiceAsync(props.invoiceId)
)

onMounted(async () => {
  refreshPreview()
  const [{ data }] = await Promise.all([
    accountingStore.fetchInvoiceByIdAsync(props.invoiceId),
    accountingStore.fetchReportColumnsAsync(),
    accountingStore.fetchReportColumnTemplatesAsync(),
  ])
  invoice.value = data
  printTemplateId.value = data.print_template
  language.value = data.language || portalStore.selectedLanguage
  settings.restore(accountingStore.printSettingTypeEnum.INVOICE, data.output_config, data.column_template)
})

const saveAsync = async (data) => {
  await accountingStore.upsertInvoiceAsync({ id: props.invoiceId, ...data })
  refreshPreview()
}
const onConfigChanged = (value) => {
  config.value = value
  saveAsync({ output_config: value, column_template: templateId.value })
}
const onConfigOptionChanged = (key, value) => onConfigChanged({ ...config.value, [key]: value })
const onSettingsApplied = () => saveAsync({ output_config: config.value, column_template: templateId.value, print_template: printTemplateId.value })

const onPrintTemplateChange = (id) => {
  printTemplateId.value = id
  saveAsync({ print_template: id })
}

const onLanguageChange = (value) => {
  language.value = value
  saveAsync({ language: value })
}

const contentOptions = computed(() => documentOptions(config.value, !isDraft.value))

const onPrintAsync = async () => {
  if (!await printAsync(`${i18n.t('common.invoice')} ${invoice.value.invoice_number}.pdf`, invoice.value.invoice_number)) return
  notifySuccess(i18n.t('common.documentPrinted'))
  emit('cancel')
}
</script>

<template>
  <print-dialog-layout
    :title="`${i18n.t('common.print')} - ${i18n.t('common.invoice')}`"
    :source="pdfSource"
    :loading="isLoading"
    :printable="!!blobUrl && !!invoice"
    :printing="isPrinting"
    @cancel="emit('cancel')"
    @print="onPrintAsync"
  >
    <template #options>
      <div v-if="invoice && config" class="q-pa-md column">
        <template v-if="!isMobile">
          <grid-detail-info :items="summary">
            <div>
              <div class="text-caption text-grey-7">{{ i18n.t('common.status') }}</div>
              <chip-template :values="getInvoiceStatusChips(invoice.status)" />
            </div>
            <div v-if="invoice.projects?.length" class="grid-detail-info__wide">
              <div class="text-caption text-grey-7">{{ i18n.t('common.project', 2) }}</div>
              <chip-template :values="getProjectChips(invoice.projects)" />
            </div>
          </grid-detail-info>
          <q-separator class="q-my-md" />
        </template>
        <q-stepper
          v-model="step"
          vertical
          animated
          header-nav
          class="q-py-none shadow-0"
          style="background: transparent;"
        >
          <q-step
            :name="1"
            :title="i18n.t('common.report.steps.content')"
            :caption="settings.caption.value"
            icon="mdi-file-document-outline"
            :done="step > 1"
          >
            <info-box v-if="lockedMessage" :message="lockedMessage" />
            <print-content-editor
              :model-value="config"
              :settings="settings"
              :options="contentOptions"
              :type-name="i18n.t('common.invoice')"
              :disable="!isDraft"
              @update:model-value="onConfigChanged"
              @update:option="onConfigOptionChanged"
              @applied="onSettingsApplied"
            />
            <stepper-navigation class="q-mt-md" :continue="nextStep" />
          </q-step>
          <q-step
            :name="2"
            :title="i18n.t('common.printConfigurator')"
            icon="mdi-printer-settings"
          >
            <print-configurator
              :document-type="accountingStore.printDocumentTypeEnum.INVOICE"
              :print-template="printTemplateId"
              :language="language"
              :locked-template-message="lockedMessage"
              @update:print-template="onPrintTemplateChange"
              @update:language="onLanguageChange"
            />
            <stepper-navigation class="q-mt-md" :back="prevStep" />
          </q-step>
        </q-stepper>
        <div v-if="isMobile" class="q-mt-md" style="height: 60vh;">
          <pdf-preview-panel :source="pdfSource" :loading="isLoading" />
        </div>
      </div>
    </template>
  </print-dialog-layout>
</template>
